import { HttpErrorResponse } from '@angular/common/http';

export interface ApiFieldError {
  /** Field path as sent by the API, camelCased and dot separated (e.g. "client.phone.number"). */
  path: string;
  message: string;
}

export interface ApiValidationError {
  messages: string[];
  fieldErrors: ApiFieldError[];
}

/**
 * Extracts the messages of an API error. Supports the shapes the Workloads API answers with:
 * a plain string (`BadRequest("...")`), ASP.NET ProblemDetails / ValidationProblemDetails
 * (`title`/`detail` and `errors: { "Client.Phone.Number": ["..."] }`) and `{ message }` / `{ error }`.
 */
export function parseApiValidationError(err: unknown): ApiValidationError {
  const body = err instanceof HttpErrorResponse ? err.error : (err as any)?.error ?? err;
  const result: ApiValidationError = { messages: [], fieldErrors: [] };

  if (typeof body === 'string') {
    if (body.trim()) result.messages.push(body.trim());
    return result;
  }

  if (body && typeof body === 'object') {
    if (body.errors && typeof body.errors === 'object') {
      for (const [key, value] of Object.entries(body.errors)) {
        const messages = (Array.isArray(value) ? value : [value]).filter(
          (m): m is string => typeof m === 'string' && !!m.trim()
        );
        for (const message of messages) {
          result.messages.push(message);
          const path = toFieldPath(key);
          if (path) result.fieldErrors.push({ path, message });
        }
      }
    }
    if (!result.messages.length) {
      const message = body.detail || body.message || body.error || body.title;
      if (typeof message === 'string' && message.trim()) result.messages.push(message.trim());
    }
  }

  return result;
}

// "Client.Phone.Number" / "$.client.phone.number" / "WorkTasks[0].Date" -> "client.phone.number" / "workTasks.0.date"
function toFieldPath(key: string): string {
  return key
    .replace(/^\$\.?/, '')
    .replace(/\[(\d+)\]/g, '.$1')
    .split('.')
    .filter(Boolean)
    .map((segment) => segment.charAt(0).toLowerCase() + segment.slice(1))
    .join('.');
}
